import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Button, MenuItem, Paper, Stack, TextField } from '@mui/material';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as clientSlice from '../../../data/client.slice';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import PageHeader from '../../controls/PageHeader';
import TextSearch from '../../controls/TextSearch';
import SearchIcon from '@mui/icons-material/Search';
import { CommonEntityState } from '../../../data/models/entity-state';
import TransportationBenefitModelList, {
  TransportationBenefitModelMenuItem,
} from './TransportationBenefitModelList';
import {
  TransportationBenefitModel,
  TransportationBenefitModelState,
} from '../../../data/models/transportation-benefit-model.model';
import ConfirmationDialog from '../../ConfirmationDialog';
import { useConfirmDialog } from '../../../hooks/useConfirmDialog';
import TransportationBenefitModelDialog from './TransportationBenefitModelDialog';

const PREFIX = 'TransBenefits';

const classes = {
  container: `${PREFIX}-container`,
  subHeader: `${PREFIX}-header`,
  contentContainer: `${PREFIX}-contentContainer`,
  search: `${PREFIX}-search`,
  statusSelect: `${PREFIX}-statusSelect`,
  addButton: `${PREFIX}-addButton`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(1),
  },
  [`& .${classes.contentContainer}`]: {
    padding: theme.spacing(2),
  },
  [`& .${classes.subHeader}`]: {
    justifyContent: 'space-between',
  },
  [`& .${classes.search}`]: {
    width: 320,
    marginRight: theme.spacing(3),
  },
  [`& .${classes.statusSelect}`]: {
    width: 200,
  },
  [`& .${classes.addButton}`]: {
    width: 150,
  },
}));

export default function TransportationBenefits() {
  const dispatch = useAppDispatch();
  const t = useCommonTranslation();
  const allStatuses = t('transBenefitModel.all');
  const isSuperReaderUser = useIsSuperReaderUser();
  const [searchText, setSearchText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(allStatuses);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogReadOnly, setDialogReadOnly] = useState(false);
  const [selectedTransBenefitModel, setSelectedTransBenefitModel] = useState<
    TransportationBenefitModel | undefined
  >(undefined);
  const confirmDialog = useConfirmDialog();

  const transBenefits = useAppSelector(
    clientSlice.selectAllTransportationBenefitModels,
  );

  useEffect(() => {
    dispatch(clientSlice.fetchTransportationBenefitModelsAsync());
    dispatch(clientSlice.fetchTransportationBenefitFunctionsAsync());
  }, []);

  useEffect(() => {
    const states =
      selectedStatus && selectedStatus !== allStatuses
        ? [selectedStatus]
        : undefined;
    dispatch(clientSlice.fetchTransportationBenefitModelsAsync(states));
  }, [selectedStatus]);

  const selectableStatuses = [
    CommonEntityState.Active,
    CommonEntityState.Inactive,
    CommonEntityState.Draft,
  ];

  const statuses = selectableStatuses.map((status) => ({
    key: status as string,
    value: t(`common.${status}`),
  }));
  statuses.unshift({ key: allStatuses, value: allStatuses });

  const activateTransBenefit = (tbm: TransportationBenefitModel) => {
    confirmDialog.request({
      title: t('transBenefitModel.confirmActivateTransBenefitTitle'),
      message: t('transBenefitModel.confirmActivateTransBenefitMessage'),
      onConfirm: () => {
        dispatch(clientSlice.activateTransportationBenefitModelAsync(tbm));
      },
    });
  };

  const deactivateTransBenefit = (tbm: TransportationBenefitModel) => {
    confirmDialog.request({
      title: t('transBenefitModel.confirmDeactivateTransBenefitTitle'),
      message: t('transBenefitModel.confirmDeactivateTransBenefitMessage'),
      onConfirm: () => {
        dispatch(clientSlice.deactivateTransportationBenefitModelAsync(tbm));
      },
    });
  };

  const deleteTransBenefit = (tbm: TransportationBenefitModel) => {
    confirmDialog.request({
      title: t('transBenefitModel.confirmDeleteTransBenefitTitle'),
      message: t('transBenefitModel.confirmDeleteTransBenefitMessage'),
      onConfirm: () => {
        dispatch(clientSlice.deleteTransportationBenefitModelAsync(tbm));
      },
    });
  };

  const handleSearchTextChange = (val: string) => {
    setSearchText(val);
  };

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
  };

  const handleAddTransBenefitClick = () => {
    setDialogOpen(true);
  };

  const handleMenuItemClick = (
    menuItem: TransportationBenefitModelMenuItem,
    transBenefitModelUUID: string,
  ) => {
    const transBenefitModel = transBenefits.find(
      (model) => model.transportationBenefitModelUUID === transBenefitModelUUID,
    );
    if (!transBenefitModel) {
      return;
    }
    switch (menuItem) {
      case TransportationBenefitModelMenuItem.Active:
        activateTransBenefit(transBenefitModel);
        break;
      case TransportationBenefitModelMenuItem.Inactive:
        deactivateTransBenefit(transBenefitModel);
        break;
      case TransportationBenefitModelMenuItem.Delete:
        deleteTransBenefit(transBenefitModel);
        break;
    }
  };

  const handleRowClick = (transBenefitModelUUID: string) => {
    const transBenefitModel = transBenefits.find(
      (model) => model.transportationBenefitModelUUID === transBenefitModelUUID,
    );
    setSelectedTransBenefitModel(transBenefitModel);
    if (
      isSuperReaderUser ||
      transBenefitModel?.state !== TransportationBenefitModelState.draft
    ) {
      setDialogReadOnly(true);
    } else {
      setDialogReadOnly(false);
    }
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogReadOnly(false);
    setSelectedTransBenefitModel(undefined);
  };

  return (
    <StyledPaper>
      <Box className={classes.container}>
        <PageHeader
          title={t('transBenefitModel.title')}
          returnLink="/settings"
          returnLinkText={t('settings.title')}
        />
        <Stack direction="column" className={classes.contentContainer}>
          <Stack direction="row" className={classes.subHeader}>
            <Stack direction="row">
              <TextSearch
                id="text-search"
                className={classes.search}
                icon={<SearchIcon />}
                label={t('transBenefitModel.search')}
                currentSearchFilter={searchText}
                onSearchChange={handleSearchTextChange}
              />
              <TextField
                select
                className={classes.statusSelect}
                label={t('transBenefitModel.status')}
                variant="outlined"
                value={selectedStatus}
                onChange={(evt) => handleStatusChange(evt.target.value)}>
                {statuses?.map((status) => {
                  return (
                    <MenuItem value={status.key} key={status.key}>
                      {status.value}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Stack>
            {!isSuperReaderUser && (
              <Button
                className={classes.addButton}
                color="primary"
                variant="contained"
                size="large"
                onClick={handleAddTransBenefitClick}>
                {t('common.add')}
              </Button>
            )}
          </Stack>
          <TransportationBenefitModelList
            data={transBenefits}
            searchText={searchText}
            handleMenuItemClick={handleMenuItemClick}
            handleRowClick={handleRowClick}
          />
        </Stack>
      </Box>
      <TransportationBenefitModelDialog
        open={dialogOpen}
        close={handleDialogClose}
        transportationBenefitModel={selectedTransBenefitModel}
        isReadOnly={dialogReadOnly}
      />
      <ConfirmationDialog {...confirmDialog.dialogProps} />
    </StyledPaper>
  );
}
